/**
 * Layer 3: GrowthBook Evaluation
 * 
 * Applies GrowthBook's native targeting rules and rollout percentages.
 * This is the final layer where user bucketing happens.
 * 
 * Also handles header/URL overrides that bypass all circuit breaker layers.
 */

import { AllowedExperimentKey, TenantId } from '@tenants/types';
import { ExperimentObservabilityService } from 'observability/services/experiment-observability';
import { ExperimentContext } from 'observability/constants/experiment-events';
import type { CircuitBreakerResult } from './types';
import { CircuitBreakerLayer } from './types';

/**
 * Handle header override (bypasses all circuit breaker layers).
 * 
 * ModHeader overrides work in all environments and bypass all circuit breaker
 * validation layers. Used for debugging and testing.
 * 
 * @param experimentKey - The experiment being overridden
 * @param headerOverride - The override value (0 = control, 1 = variation)
 * @param tenantId - The tenant ID for tracking
 * @param isEnabledForTenant - Whether experiment is enabled in tenant config
 * @returns CircuitBreakerResult with override decision
 */
export function handleHeaderOverride(
  experimentKey: AllowedExperimentKey,
  headerOverride: number,
  tenantId: TenantId | undefined,
  isEnabledForTenant: boolean
): CircuitBreakerResult {
  const allowed = headerOverride === 1;
  
  ExperimentObservabilityService.trackOverrideApplied({
    experimentKey,
    context: ExperimentContext.SSR,
    result: allowed,
    tenantId,
  });

  return {
    allowed,
    variationId: headerOverride,
    layer: CircuitBreakerLayer.OVERRIDE,
    reason: 'ModHeader override applied',
    isEnabledForTenant,
  };
}

/**
 * Handle URL override (bypasses all circuit breaker layers).
 * 
 * URL overrides only work in development and preview environments.
 * They bypass all circuit breaker validation layers for debugging.
 * 
 * @param _experimentKey - The experiment being overridden (unused, kept for API consistency)
 * @param urlOverride - The override value (0 = control, 1 = variation)
 * @param isEnabledForTenant - Whether experiment is enabled in tenant config
 * @returns CircuitBreakerResult with override decision
 */
export function handleUrlOverride(
  _experimentKey: AllowedExperimentKey,
  urlOverride: number,
  isEnabledForTenant: boolean
): CircuitBreakerResult {
  const allowed = urlOverride === 1;
  
  // Note: URL override tracking happens in use-url-override.ts
  // Logging happens in growthbook-ssr-payload.ts
  return {
    allowed,
    variationId: urlOverride,
    layer: CircuitBreakerLayer.OVERRIDE,
    reason: `URL override: ${allowed ? 'variation' : 'control'}`,
    isEnabledForTenant,
  };
}

/**
 * Evaluate GrowthBook result (final circuit breaker layer).
 * 
 * This is the third and final layer where GrowthBook's native targeting
 * rules and rollout percentages are applied to determine user bucketing.
 * 
 * @param experimentKey - The experiment being evaluated
 * @param growthBookResult - The result from GrowthBook's run() method
 * @param tenantId - The tenant ID for tracking
 * @param context - Whether this is SSR or client-side evaluation
 * @returns CircuitBreakerResult with GrowthBook's decision
 */
export function evaluateGrowthBookResult(
  experimentKey: AllowedExperimentKey,
  growthBookResult: { variationId: number; inExperiment: boolean; hashUsed?: boolean; value?: unknown } | undefined,
  tenantId: TenantId | undefined,
  context: ExperimentContext
): CircuitBreakerResult {
  // Handle undefined result (GrowthBook failed to evaluate)
  if (!growthBookResult) {
    return {
      allowed: false,
      variationId: 0,
      layer: CircuitBreakerLayer.GROWTHBOOK_EVALUATION,
      reason: 'GrowthBook evaluation failed (undefined result)',
      isEnabledForTenant: true,
    };
  }

  // Prefer the actual served value over the variation index: GrowthBook lets
  // two different variation indexes serve the same value (e.g. [false, false]
  // to intentionally hold an experiment at 100% control), and in that case
  // "index === 1" would incorrectly flag ~half of users as "in variation".
  const allowed = 'value' in growthBookResult && growthBookResult.value !== undefined
    ? Boolean(growthBookResult.value)
    : growthBookResult.variationId === 1;
  
  ExperimentObservabilityService.trackGrowthBookEvaluation({
    experimentKey,
    context,
    result: allowed,
    tenantId,
    variationId: growthBookResult.variationId,
    inExperiment: growthBookResult.inExperiment,
    hashUsed: growthBookResult.hashUsed,
  });

  return {
    allowed,
    variationId: growthBookResult.variationId,
    layer: CircuitBreakerLayer.GROWTHBOOK_EVALUATION,
    reason: allowed ? 'User in variation bucket' : 'User in control bucket',
    isEnabledForTenant: true,
  };
}

/**
 * Apply SSR result (already evaluated through GrowthBook).
 * 
 * Used by client-side code to apply the SSR-evaluated result
 * without re-running GrowthBook evaluation. The tenant config
 * check is still applied.
 * 
 * @param ssrResult - The SSR evaluation result (true = variation, false = control)
 * @param isEnabledForTenant - Whether experiment is enabled in tenant config
 * @returns CircuitBreakerResult with SSR decision
 */
export function applySSRResult(
  ssrResult: boolean,
  isEnabledForTenant: boolean
): CircuitBreakerResult {
  const variationId = ssrResult ? 1 : 0;
  const reason = ssrResult ? 'GrowthBook evaluation: variation bucket' : 'GrowthBook evaluation: control bucket';
  
  // Logging happens in use-experiment-variation.ts (final decision)
  return {
    allowed: ssrResult,
    variationId,
    layer: CircuitBreakerLayer.GROWTHBOOK_EVALUATION,
    reason,
    isEnabledForTenant,
  };
}
